import moment from "moment-timezone";
import API from "serviceshift-ui/api-client";
import { computeClockSkewMs } from "@/lib/clockSkew";

import { formatDuration } from "@/mixins";

const TASK_STATUSES_WITH_EXTRA_DATA = {
  Enqueued: {
    color: "var(--background-task-enqueued)",
    icon: "fa-layer-group",
    statusName: "Queued"
  },
  Processing: {
    color: "var(--background-task-processing)",
    icon: "fa-hourglass-start",
    statusName: "In Progress"
  },
  Ready: {
    color: "var( --background-task-ready)",
    icon: "fa-check",
    statusName: "Completed"
  },
  Failed: {
    color: "var(--background-task-failed)",
    icon: "fa-frown",
    statusName: "Failed"
  },
  ReadyWarning: {
    color: "var( --background-task-ready)",
    icon: "fa-triangle-exclamation",
    statusName: "Completed"
  },
  // A run stopped by a vendor's request quota. It resumes on its own, so a red
  // "Failed" frown reads as something the admin has to go and fix when there is
  // nothing to do.
  Paused: {
    color: "var(--background-task-processing)",
    icon: "fa-clock",
    statusName: "Paused"
  }
};

const getTime = (date, status) =>
  date ? moment(date).format(`[${status} at] hh:mm a`) : null;
const getDate = (date) => (date ? moment(date).format("MM/DD/YYYY") : null);
const getTotalTime = (start, end) => {
  const duration = moment.duration(moment(end).diff(moment(start)));

  return formatDuration(duration.minutes(), true);
};

// Matches the server's not_expired scope, which keeps a task only while
// expires_at > now(). Unparseable dates count as not expired so bad data never
// hides a task.
export function isTaskExpired(task, serverNowMs) {
  if (!task.expiresAt) return false;

  const expiresAtMs = new Date(task.expiresAt).getTime();
  return !Number.isNaN(expiresAtMs) && expiresAtMs <= serverNowMs;
}

export function taskAdapter(task) {
  let status = task.status;
  // Shows complete with warning icon for pricebook import with row errors
  if (task?.message?.includes("view the rows with errors")) {
    status = "ReadyWarning";
  }
  // A run recorded as failed so its run-once guard keeps the tenant eligible, but
  // which resumes on its own. The server decides this, not a string match here.
  // Only the presentation changes — isCompleted/isActive still read task.status, so
  // the task stays acknowledgeable exactly as before.
  if (task?.paused) {
    status = "Paused";
  }
  return {
    ...task,
    ...TASK_STATUSES_WITH_EXTRA_DATA[status],
    enqueuedDate: getDate(task.enqueuedAt),
    startDate: getDate(task.startedAt),
    finishDate: getDate(task.finishedAt),
    enqueuedTime: getTime(task.enqueuedAt, "Queued"),
    startedTime: getTime(task.startedAt, "Started"),
    completedTime: getTime(task.finishedAt, "Completed"),
    failedTime: getTime(task.finishedAt, "Failed"),
    linkExpiresAt: task.downloadUrl
      ? moment(task.expiresAt).format(`[Expires on] MM/DD/YYYY [at] hh:mm a`)
      : null,
    isNew: task.status === "Enqueued",
    isCompleted: task.status === "Ready" || task.status === "Failed",
    isVisible: task.status !== "Expired",
    isAcknowledged: task.acknowledgedAt !== undefined,
    isActive:
      (task.status === "Ready" || task.status === "Failed") &&
      task.acknowledgedAt === undefined,
    totalTime: getTotalTime(task.enqueuedAt, task.finishedAt)
  };
}

export default {
  getBackgroundTasks({ commit }) {
    return API.makeRequest("/admins/background_tasks.json", {
      method: "GET"
    }).then((res) => {
      // Expired tasks are pruned on the browser's clock, so anchor it to the
      // server's or a browser running fast hides links that still work.
      commit("replace", {
        state: "backgroundTasksClockSkewMs",
        data: computeClockSkewMs(res.headers?.date)
      });
      commit("replace", {
        state: "backgroundTasks",
        data: (res.data.backgroundTasks || [])
          .sort(
            (a, b) =>
              new Date(b.enqueuedAt).valueOf() -
              new Date(a.enqueuedAt).valueOf()
          )
          .map(taskAdapter)
          .filter((task) => task.isVisible)
      });
    });
  },
  acknowledgeBackgroundTasks() {
    return API.makeRequest("/admins/background_tasks/acknowledge", {
      method: "PATCH"
    });
  },
  expireBackgroundTask({ commit, state }, id) {
    return API.makeRequest(`/admins/background_tasks/${id}`, {
      method: "DELETE"
    }).then(() => {
      commit("replace", {
        state: "backgroundTasks",
        data: state.backgroundTasks.filter((task) => task.id !== id)
      });
    });
  },
  // Resolves to whether anything was removed, so callers can skip work on the
  // common no-op tick.
  pruneExpiredBackgroundTasks({ commit, state }) {
    const serverNowMs = Date.now() + state.backgroundTasksClockSkewMs;
    const kept = state.backgroundTasks.filter(
      (task) => !isTaskExpired(task, serverNowMs)
    );
    if (kept.length === state.backgroundTasks.length) return false;

    commit("replace", { state: "backgroundTasks", data: kept });
    return true;
  },
  updateBackgroundTasks({ commit, state }, task) {
    if (!task) {
      return;
    }

    const existingTask = state.backgroundTasks.find((t) => t.id === task.id);
    const updatedTask = { ...existingTask, ...taskAdapter(task) };

    // Ignore any messages received too late out of order
    if (existingTask && existingTask.updatedAt > updatedTask.updatedAt) {
      return;
    }

    const updatedList = existingTask
      ? state.backgroundTasks.map((t) =>
          t.id === updatedTask.id ? updatedTask : t
        )
      : [updatedTask, ...state.backgroundTasks];

    return commit("replace", {
      state: "backgroundTasks",
      data: updatedList.filter((task) => task.isVisible)
    });
  }
};
